import React from 'react';
import { Icon } from '@iconify-icon/react';

const useCases = [
  {
    icon: 'tabler:user-heart',
    kicker: 'People & culture',
    title: 'HR & onboarding',
    body: 'New hires get instant answers on benefits, PTO, and policies — with citations to the handbook so managers stop being the FAQ.',
  },
  {
    icon: 'tabler:headset',
    kicker: 'Customer facing',
    title: 'Support & success',
    body: 'Agents pull the right SOP, product note, or escalation path in seconds. Fewer tickets bounce; more answers stay consistent.',
  },
  {
    icon: 'tabler:scale',
    kicker: 'Risk & counsel',
    title: 'Legal & compliance',
    body: 'Ask what the policy actually says — and click through to the clause. Keep privileged work in a sealed environment, not a consumer chatbot.',
  },
  {
    icon: 'tabler:building-store',
    kicker: 'Go-to-market',
    title: 'Sales enablement',
    body: 'Battle cards, pricing rules, competitive notes, and proposal language — always the latest version, always attributable.',
  },
  {
    icon: 'tabler:building-factory-2',
    kicker: 'Operations',
    title: 'Ops & procedures',
    body: 'Shift handoffs, safety checklists, and runbooks stop living in binders. Floor teams ask once and get the current procedure.',
  },
  {
    icon: 'tabler:code',
    kicker: 'Builders',
    title: 'Engineering & IT',
    body: 'Architecture decisions, runbooks, and internal APIs become searchable knowledge — so tribal context survives team changes.',
  },
];

const UseCases: React.FC = () => (
  <section aria-label="Company brain use cases" className="bg-white py-16 sm:py-20">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl">
        <div className="flex items-center gap-4">
          <span className="h-px w-10 bg-primary-600/50" />
          <span className="text-[11px] font-semibold uppercase tracking-[0.24em] text-primary-600">
            Use cases
          </span>
        </div>
        <h2 className="mt-5 text-3xl font-bold leading-tight tracking-tight text-gray-900 sm:text-[2.6rem] sm:leading-[1.15]">
          One Company Brain.{' '}
          <span className="font-display font-medium italic text-primary-600">
            Every department.
          </span>
        </h2>
        <p className="mt-5 text-lg leading-relaxed text-gray-500">
          The same knowledge base can power HR, support, legal, sales, and ops — each team asks in
          their own language and gets answers grounded in your documents.
        </p>
      </div>

      <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {useCases.map((uc) => (
          <div
            key={uc.title}
            className="flex flex-col rounded-[28px] border border-gray-100 bg-[#f8fafc] p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-md">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white shadow-sm">
              <Icon icon={uc.icon} className="text-2xl text-primary-600" />
            </div>
            <div className="mt-5 text-[10px] font-bold uppercase tracking-[0.24em] text-primary-600">
              {uc.kicker}
            </div>
            <h3 className="mt-1.5 text-xl font-bold text-gray-900">{uc.title}</h3>
            <p className="mt-3 text-sm leading-relaxed text-gray-500">{uc.body}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export default UseCases;
